import type { ProductPresentationSource } from "./productPresentation";

export const FRAME_MEASUREMENT_FIELDS = [
  { key: "lens", label: "Lens width" },
  { key: "bridge", label: "Bridge width" },
  { key: "temple", label: "Temple length" },
] as const;

export type FrameMeasurementKey =
  (typeof FRAME_MEASUREMENT_FIELDS)[number]["key"];

export type FrameMeasurements = Record<FrameMeasurementKey, number | null>;

export type AdjustableNosepadsStatus = "yes" | "no" | "unknown";

const EMPTY_MEASUREMENTS: FrameMeasurements = {
  lens: null,
  bridge: null,
  temple: null,
};

/**
 * Accept only a positive millimetre measurement. Blank and malformed input
 * deliberately remain unavailable instead of being coerced to zero.
 */
export function parseFrameMeasurement(value: unknown): number | null {
  const normalized = String(value ?? "").trim();
  const match = normalized.match(/^(\d+(?:\.\d+)?)\s*(?:mm)?$/i);
  if (!match) return null;

  const measurement = Number(match[1]);
  return Number.isFinite(measurement) && measurement > 0 ? measurement : null;
}

export function isInvalidFrameMeasurement(value: unknown): boolean {
  return (
    String(value ?? "").trim() !== "" && parseFrameMeasurement(value) === null
  );
}

export function toFrameMeasurements(
  values: Record<string, unknown>
): FrameMeasurements {
  return FRAME_MEASUREMENT_FIELDS.reduce<FrameMeasurements>(
    (measurements, { key }) => {
      measurements[key] = parseFrameMeasurement(values[key]);
      return measurements;
    },
    { ...EMPTY_MEASUREMENTS }
  );
}

/** This pair minus your frame; null means one of the values is unavailable. */
export function getFrameMeasurementDifference(
  yourMeasurement: number | null,
  thisPairMeasurement: number | null
): number | null {
  if (yourMeasurement === null || thisPairMeasurement === null) return null;
  return Math.round((thisPairMeasurement - yourMeasurement) * 100) / 100;
}

export function formatFrameMeasurement(
  measurement: number | null,
  options: { signed?: boolean } = {}
): string {
  if (measurement === null) return "Unavailable";

  const value = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(Math.abs(measurement));
  const sign =
    options.signed && measurement > 0 ? "+" : measurement < 0 ? "−" : "";
  return `${sign}${value} mm`;
}

/** Reference ranges compare dimensions, never diagnose fit. See design-refinement/RESEARCH.md. */
export function getFrameMeasurementComparison(
  key: FrameMeasurementKey,
  yours: number | null,
  pair: number | null,
  nosepads: AdjustableNosepadsStatus
): { tone: "close" | "check" | "neutral"; difference: string; label: string } {
  const delta = getFrameMeasurementDifference(yours, pair);
  if (delta === null) {
    return {
      tone: "neutral",
      difference: "—",
      label: pair === null ? "Not listed" : "Enter your size",
    };
  }
  const direction =
    delta === 0
      ? "Same measurement"
      : `${formatFrameMeasurement(Math.abs(delta))} ${
          key === "temple"
            ? delta > 0
              ? "longer"
              : "shorter"
            : delta > 0
            ? "wider"
            : "narrower"
        }`;
  if (key === "bridge" && nosepads === "unknown") {
    return {
      tone: "neutral",
      difference: direction,
      label: "Check bridge design",
    };
  }
  const limit =
    key === "lens" ? 3 : key === "temple" ? 5 : nosepads === "yes" ? 2 : 1;
  const close = Math.abs(delta) <= limit;
  return {
    tone: close ? "close" : "check",
    difference: direction,
    label: close ? "Close to your frame" : "Worth checking",
  };
}

/**
 * The wider bridge allowance is available only for a catalog field that
 * explicitly answers "Yes". Missing or other values must not be inferred.
 */
export function getAdjustableNosepadsStatus(
  product: ProductPresentationSource | null | undefined
): AdjustableNosepadsStatus {
  const attribute = product?.attributes?.find(
    (item) =>
      String(item?.name ?? "")
        .replace(/:\s*$/, "")
        .trim()
        .toLocaleLowerCase("en-US") === "adjustable nosepads"
  );
  if (!attribute) return "unknown";

  const options = Array.isArray(attribute.options)
    ? attribute.options
    : [attribute.options];
  const value = options
    .map((option) => String(option ?? "").trim())
    .filter(Boolean)
    .join(", ")
    .toLocaleLowerCase("en-US");

  if (value === "yes") return "yes";
  if (value === "no") return "no";
  return "unknown";
}
